"use client";

export default function ReflectionSection() {
  return (
    <section
      className="rounded-2xl overflow-hidden fade-in-up"
      style={{
        background: "linear-gradient(135deg, rgba(0,18,88,0.6) 0%, rgba(54,23,64,0.8) 100%)",
        border: "1px solid rgba(219,184,77,0.3)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
      }}
    >
      {/* Header bar */}
      <div
        className="px-7 py-5"
        style={{
          background: "linear-gradient(135deg, rgba(124,62,97,0.4) 0%, rgba(0,18,88,0.5) 100%)",
          borderBottom: "1px solid rgba(219,184,77,0.2)",
        }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: "linear-gradient(135deg, #7c3e61, #dbb84d)",
            }}
          >
            <span className="text-sm">✦</span>
          </div>
          <div>
            <h2
              className="text-lg font-display text-white/95"
              style={{ fontWeight: 700 }}
            >
              Reflection & Integration
            </h2>
            <p className="text-xs text-white/40 mt-0.5" style={{ fontWeight: 400 }}>
              A closing guide for every QuantuMe Nautilus journey
            </p>
          </div>
        </div>
      </div>
      {/* Content */}
      <div className="p-7 md:p-8 space-y-6">

        {/* Opening */}
        <p className="text-sm md:text-base text-white/70 leading-relaxed" style={{ fontWeight: 400 }}>
          Your Avatar Blueprint is more than a report — it is an invitation. An invitation to see yourself more clearly, to act with greater intention, and to step more fully into the life that is uniquely yours.
        </p>

        <div className="section-divider" />

        {/* Three Reflection Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Pillar 1 */}
          <div
            className="rounded-xl p-5"
            style={{
              background: "rgba(219,184,77,0.06)",
              border: "1px solid rgba(219,184,77,0.2)",
            }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(219,184,77,0.2) 0%, rgba(124,62,97,0.3) 100%)",
                border: "1px solid rgba(219,184,77,0.35)",
              }}
            >
              <span className="text-base">🌱</span>
            </div>
            <h4
              className="text-sm font-display mb-2 gold-text"
              style={{ fontWeight: 700 }}
            >
              Sit with It
            </h4>
            <p className="text-xs text-white/60 leading-relaxed" style={{ fontWeight: 400 }}>
              Read your Blueprint slowly. Let the insights settle. You don't need to act on everything at once. Some truths need time to take root before they can grow. IMPORTANT: Don't forget to save the report to PDF before closing this page!
            </p>
          </div>

          {/* Pillar 2 */}
          <div
            className="rounded-xl p-5"
            style={{
              background: "rgba(124,62,97,0.1)",
              border: "1px solid rgba(124,62,97,0.3)",
            }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(124,62,97,0.3) 0%, rgba(219,184,77,0.15) 100%)",
                border: "1px solid rgba(124,62,97,0.4)",
              }}
            >
              <span className="text-base">📓</span>
            </div>
            <h4
              className="text-sm font-display mb-2"
              style={{ fontWeight: 700, color: "#b87fa0" }}
            >
              Journal Your Responses
            </h4>
            <p className="text-xs text-white/60 leading-relaxed" style={{ fontWeight: 400 }}>
              Write down what resonates most strongly. Notice what surprises you, what feels true, and what you'd like to explore further. Your reactions are data.
            </p>
          </div>

          {/* Pillar 3 */}
          <div
            className="rounded-xl p-5"
            style={{
              background: "rgba(0,18,88,0.3)",
              border: "1px solid rgba(219,184,77,0.15)",
            }}
          >
            <div
              className="w-9 h-9 rounded-full flex items-center justify-center mb-4"
              style={{
                background: "linear-gradient(135deg, rgba(0,18,88,0.5) 0%, rgba(219,184,77,0.1) 100%)",
                border: "1px solid rgba(219,184,77,0.2)",
              }}
            >
              <span className="text-base">🧭</span>
            </div>
            <h4
              className="text-sm font-display mb-2 gold-text"
              style={{ fontWeight: 700 }}
            >
              Return Often
            </h4>
            <p className="text-xs text-white/60 leading-relaxed" style={{ fontWeight: 400 }}>
              Your Blueprint doesn't expire. Return to it at crossroads, during transitions, or whenever you feel unclear about your direction. It will show you something new each time.
            </p>
          </div>
        </div>

        <div className="section-divider" />

        {/* Deeper Questions */}
        <div>
          <h3
            className="text-base font-display text-white/90 mb-4"
            style={{ fontWeight: 600 }}
          >
            Questions to Carry Forward
          </h3>
          <div className="space-y-3">
            {[
              "Where in my life am I most fully myself — and where am I holding back?",
              "What would change if I fully trusted the strengths described in my Blueprint?",
              "Which growth challenge is asking for my attention right now?",
              "What does my 30-Day Plan reveal about what I've been avoiding?",
              "If I embodied my Avatar Statement completely, what would my life look like in one year?",
            ]?.map((question, i) => (
              <div
                key={i}
                className="flex items-start gap-3 py-3 border-b"
                style={{ borderColor: "rgba(219,184,77,0.08)" }}
              >
                <span
                  className="text-xs font-mono font-500 mt-0.5 flex-shrink-0"
                  style={{ color: "rgba(219,184,77,0.5)", fontVariantNumeric: "tabular-nums" }}
                >
                  0{i + 1}
                </span>
                <p className="text-sm text-white/65 leading-relaxed italic" style={{ fontWeight: 400 }}>
                  {question}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="section-divider" />

        {/* Closing */}
        <div
          className="rounded-xl p-6 text-center"
          style={{
            background: "linear-gradient(135deg, rgba(219,184,77,0.08) 0%, rgba(124,62,97,0.12) 100%)",
            border: "1px solid rgba(219,184,77,0.2)",
          }}
        >
          <p
            className="text-base md:text-lg font-display text-white/90 leading-relaxed italic mb-3"
            style={{ fontWeight: 500 }}
          >
            "The greatest journey you will ever take is the one that leads you back to yourself."
          </p>
          <p className="text-xs text-white/40" style={{ fontWeight: 400 }}>
            Your QuantuMe Nautilus Blueprint is a living document. It grows with you.
          </p>
        </div>

        {/* Timestamp */}
        <p
          className="text-xs text-center font-mono"
          style={{ color: "rgba(219,184,77,0.3)", fontVariantNumeric: "tabular-nums" }}
        >
          Blueprint generated: March 13, 2026 · QuantuMe Nautilus v2.4
        </p>
      </div>
    </section>
  );
}